import { useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import getRecommendations from "../../api/mightLikeApi";
import { useIsInStockContext } from "../../context/isInStock/IsInStockContext";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useStockPrice } from "../../context/stockPrice/StockPriceContext";
import {
  isProductDiscontinued,
  isProductUnavailableForUS,
} from "../../../utils/productUtils";

import {
  matchAdvertsToMightLikeProducts,
  getVariantIdCollection,
} from "../../../common/CriteoAdvertsForRecs";
import criteoApi from "../../api/criteoApi";
import getProductSummaries from "../../api/productSummariesApi";
import useAdverts from "../../../client/hooks/useAdverts";

import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";
import { YOU_MIGHT_LIKE_SECTION_ADS_CONFIG } from "../../constants";

const hasCriteoResponse = (data) =>
  Array.isArray(data?.products) && data.products.length > 0;

const buildChildToParentIdMap = (productsWithVariants) => {
  const childToParentIdMap = new Map();

  (productsWithVariants || []).forEach((product) => {
    const parentId = Number(product?.id);
    if (!Number.isFinite(parentId)) {
      return;
    }

    // Parent IDs should resolve to themselves when no translation is needed.
    childToParentIdMap.set(parentId, parentId);

    (product?.variants || []).forEach((variant) => {
      const childId = Number(variant?.id);
      if (Number.isFinite(childId)) {
        childToParentIdMap.set(childId, parentId);
      }

      const colourWayId = Number(variant?.colourWayId);
      if (
        Number.isFinite(colourWayId) &&
        !childToParentIdMap.has(colourWayId)
      ) {
        childToParentIdMap.set(colourWayId, parentId);
      }
    });
  });

  return childToParentIdMap;
};

export default function useRecommendations(
  { productId, scope, pageArea },
  hasCriteoAdverts = false,
) {
  const { isStockStateReady, isInStock } = useIsInStockContext();
  const { isMixAndMatch, product, storeCode } = useRuntimeConstants();
  const isDiscontinued = isProductDiscontinued(product);
  const isUnavailableForUS = isProductUnavailableForUS(product, storeCode);
  const [mightLikeProducts, setMightLikeProducts] = useState(null);
  const [recsRequestId, setRecsRequestId] = useState("");
  const [hasFetchedRecs, setHasFetchedRecs] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);
  const { advertsSdkInstance } = useAdverts();
  const [mightLikeProductsWithAdverts, setMightLikeProductsWithAdverts] =
    useState(null);
  const [hasCriteoAds, setHasCriteoAds] = useState(false);
  const { products: productPricing } = useStockPrice();
  const productPrice = productPricing?.price;
  const previousProductIdRef = useRef(productId);

  const getVariantIds = (products, productsWithVariantIdsCollection) => {
    try {
      return getVariantIdCollection(products, productsWithVariantIdsCollection);
    } catch (variantError) {
      logError(ERROR_TYPE.NonCriticalAppError, {
        message: variantError.message,
        action: "GetVariantIdCollection",
      });
      return [];
    }
  };

  useEffect(() => {
    if (isMixAndMatch || !isStockStateReady || !productId) {
      return;
    }

    let isCancelled = false;

    const safeStateSetter = (setter, value) => {
      if (!isCancelled) {
        setter(value);
      }
    };

    const setAdvertsFallback = (products = []) => {
      const fallbackProducts = products || [];

      safeStateSetter(setMightLikeProducts, fallbackProducts);
      safeStateSetter(setMightLikeProductsWithAdverts, fallbackProducts);
      safeStateSetter(setHasCriteoAds, false);
    };

    const fetchRecommendations = async () => {
      try {
        const response = await getRecommendations({
          isInStock,
          isDiscontinued,
          isUnavailableForUS,
          scope,
          productId,
          pageArea,
          handleErrorReturnValue: () => {
            safeStateSetter(setApiFailed, true);
          },
        });
        const products = response?.recommendations;

        unstable_batchedUpdates(() => {
          safeStateSetter(setMightLikeProducts, products);
          safeStateSetter(setRecsRequestId, response?.recsRequestId ?? "");
          safeStateSetter(setHasFetchedRecs, true);
        });

        if (!hasCriteoAdverts) {
          setAdvertsFallback(products);
          return;
        }

        if (!products?.length) {
          setAdvertsFallback();
          return;
        }

        const productsWithVariantIdsCollection =
          await getProductSummaries(products);

        const variantIds = getVariantIds(
          products,
          productsWithVariantIdsCollection,
        );
        const childToParentIdMap = buildChildToParentIdMap(
          productsWithVariantIdsCollection,
        );

        if (!variantIds?.length) {
          setAdvertsFallback(products);
          return;
        }

        const criteoAdData = await criteoApi({
          productIdCollection: variantIds,
          productId,
          isInStock,
          price: productPrice,
          advertsSdkInstance,
          adsConfig: YOU_MIGHT_LIKE_SECTION_ADS_CONFIG,
        });

        if (!hasCriteoResponse(criteoAdData)) {
          setAdvertsFallback(products);
          return;
        }

        const { OnLoadBeacon, OnViewBeacon, responseId } = criteoAdData;
        const containerBeacons = {
          OnLoadBeacon,
          OnViewBeacon,
          responseId,
        };

        const productsWithAdverts = matchAdvertsToMightLikeProducts(
          products,
          criteoAdData,
          containerBeacons,
          YOU_MIGHT_LIKE_SECTION_ADS_CONFIG,
          childToParentIdMap,
        );

        const hasMatchedSponsoredProducts = Boolean(
          productsWithAdverts?.some((productItem) => productItem.advertisement),
        );

        if (productsWithAdverts) {
          safeStateSetter(setMightLikeProductsWithAdverts, productsWithAdverts);
          safeStateSetter(setHasCriteoAds, hasMatchedSponsoredProducts);

          return;
        }

        setAdvertsFallback(products);
      } catch (error) {
        logError(ERROR_TYPE.NonCriticalAppError, {
          message: error.message,
          action: "useRecommendationsCall",
        });
        safeStateSetter(setApiFailed, true);
      }
    };

    fetchRecommendations();

    return () => {
      isCancelled = true;
    };
  }, [
    hasCriteoAdverts,
    advertsSdkInstance,
    isMixAndMatch,
    isStockStateReady,
    isInStock,
    productId,
    productPrice,
    isDiscontinued,
    isUnavailableForUS,
    scope,
    pageArea,
  ]);

  useEffect(() => {
    if (productId !== previousProductIdRef.current) {
      setApiFailed(false);
      setHasFetchedRecs(false);
      setHasCriteoAds(false);
      setMightLikeProducts(null);
      setMightLikeProductsWithAdverts(null);
      setRecsRequestId("");
      previousProductIdRef.current = productId;
    }
  }, [productId]);

  return {
    mightLikeProducts,
    recsRequestId,
    hasFetchedRecs,
    apiFailed,
    hasCriteoAds,
    mightLikeProductsWithAdverts,
  };
}
